import React, { useState, useEffect } from 'react';
import { XIcon, GitBranchIcon, ArrowRightIcon } from 'lucide-react';

/**
 * Modal for viewing differences between sheet versions
 */
const VersionDiffModal = ({ isOpen, onClose, sheetId, fromVersion, toVersion }) => {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch diff data when modal opens
  useEffect(() => {
    if (isOpen && sheetId && fromVersion && toVersion) {
      fetchDiff();
    }
  }, [isOpen, sheetId, fromVersion, toVersion]);

  const fetchDiff = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/sheets/${sheetId}/versions/diff?from=${fromVersion}&to=${toVersion}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        setDiff(result.data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch diff');
      }
    } catch (err) {
      console.error('Error fetching diff:', err);
      setError('Failed to fetch version differences');
    } finally {
      setLoading(false);
    }
  };

  // Convert technical field names to user-friendly labels
  const getFieldLabel = (fieldPath) => {
    const fieldLabels = {
      'transposeValue': 'Transposition',
      'title': 'Song Title',
      'artist': 'Artist',
      'bpm': 'BPM (Beats Per Minute)',
      'status': 'Status',
      'sections': 'Sheet Sections',
      'partsModule': 'Chord Progressions',
      'latest_version_id': 'Latest Version ID',
      'updatedAt': 'Last Modified',
      'dateModified': 'Date Modified'
    };
    
    return fieldLabels[fieldPath] || fieldPath;
  };

  // Format values for better display
  const formatValue = (value, fieldPath) => {
    if (fieldPath === 'transposeValue') {
      if (value === 0) return 'No transposition (Original key)';
      return value > 0 ? `+${value} semitones` : `${value} semitones`;
    }
    
    if (fieldPath === 'status') {
      return value === 'Ready' ? '✓ Ready' : '⚠ Work in Progress';
    }
    
    if (fieldPath === 'updatedAt' || fieldPath === 'dateModified') {
      return new Date(value).toLocaleString();
    }
    
    if (typeof value === 'string' && value.length > 50) {
      return value.substring(0, 50) + '...';
    }
    
    return JSON.stringify(value);
  };

  const renderDiffSection = (section, path = '') => {
    if (!section || typeof section !== 'object') {
      return null;
    }

    return Object.entries(section).map(([key, value]) => {
      const currentPath = path ? `${path}.${key}` : key;
      const fieldLabel = getFieldLabel(currentPath);
      
      if (Array.isArray(value)) {
        // Handle array changes (additions, deletions, modifications)
        if (value.length === 3 && value[2] === 0) {
          // Deletion
          return (
            <div key={currentPath} className="mb-2 p-2 bg-red-50 border-l-4 border-red-400 rounded">
              <div className="text-sm font-medium text-red-800">
                Removed: {fieldLabel}
              </div>
              <div className="text-xs text-red-600 mt-1">
                <span className="line-through">{formatValue(value[0], currentPath)}</span>
              </div>
            </div>
          );
        } else if (value.length === 3 && value[2] === 1) {
          // Addition
          return (
            <div key={currentPath} className="mb-2 p-2 bg-green-50 border-l-4 border-green-400 rounded">
              <div className="text-sm font-medium text-green-800">
                Added: {fieldLabel}
              </div>
              <div className="text-xs text-green-600 mt-1">
                {formatValue(value[0], currentPath)}
              </div>
            </div>
          );
        } else if (value.length === 2) {
          // Modification
          return (
            <div key={currentPath} className="mb-2 p-2 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <div className="text-sm font-medium text-yellow-800">
                Modified: {fieldLabel}
              </div>
              <div className="text-xs text-yellow-600 mt-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="bg-red-100 px-2 py-1 rounded line-through">
                    {formatValue(value[0], currentPath)}
                  </span>
                  <ArrowRightIcon className="w-3 h-3 flex-shrink-0" />
                  <span className="bg-green-100 px-2 py-1 rounded">
                    {formatValue(value[1], currentPath)}
                  </span>
                </div>
              </div>
            </div>
          );
        }
      } else if (typeof value === 'object' && value !== null) {
        // Nested object changes
        return (
          <div key={currentPath} className="mb-2">
            <div className="text-sm font-medium text-gray-700 mb-1">
              Changes in {fieldLabel}:
            </div>
            <div className="ml-4">
              {renderDiffSection(value, currentPath)}
            </div>
          </div>
        );
      }
      
      return null;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <GitBranchIcon className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Version Comparison
            </h2>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span className="bg-red-100 text-red-700 px-2 py-1 rounded">v{fromVersion}</span>
              <ArrowRightIcon className="w-4 h-4" />
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded">v{toVersion}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">Loading version differences...</div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-red-800 font-medium">Error</div>
              <div className="text-red-600 text-sm mt-1">{error}</div>
            </div>
          ) : diff ? (
            <div>
              {Object.keys(diff).length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No differences found between these versions
                </div>
              ) : (
                <div>
                  <div className="mb-4 text-sm text-gray-600">
                    Showing changes from version {fromVersion} to version {toVersion}
                  </div>
                  {renderDiffSection(diff)}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No diff data available
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-500">
              Red = Removed • Green = Added • Yellow = Modified
            </div>
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionDiffModal;